import axios from "axios";
import { isEmpty, omit, omitBy, isNil, map } from "lodash";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import normalize from "Utils/normalizer";
import { DefaultFilter, Pagination, Sorting } from "Types/common.types";
import { apiURL } from "Helpers/url";

const url = apiURL("/report/delivery");

interface DeliveryReportFilters extends DefaultFilter {
  fromDate?: string;
  toDate?: string;
  companyOrgID?: string;
  status?: string;
  orFields?: string;
}

export const listTrainReports = async (
  filters?: DeliveryReportFilters,
  pagination?: Pagination | null,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/train/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.trainReports;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTrainReports(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listTrolleyReports = async (
  filters?: DeliveryReportFilters,
  pagination?: Pagination | null,
  sorting?: Sorting,
  signal?,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.trolleyReports;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTrolleyReports(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listBoxReports = async (
  filters?: DeliveryReportFilters,
  pagination?: Pagination | null,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.boxReports;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listBoxReports(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listParcelsReports = async (
  filters?: DeliveryReportFilters,
  pagination?: Pagination | null,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/parcel/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.orderReports;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listParcelsReports(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listTripReports = async (
  filters?: DeliveryReportFilters,
  pagination?: Pagination | null,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: apiURL("/trip_report/list"),
      data: omitBy(
        {
          filter: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.tripReport;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTripReports(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listTripDetails = async (
  filters: DeliveryReportFilters,
  pagination: Pagination,
  sorting: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: apiURL("/trip_report/detail/list"),
      data: omitBy(
        {
          filter: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.tripReport;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTripDetails(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
